import { useState, useEffect } from "react";
import { saveVendorInvoice, fetchProjects, fetchItems } from "../Api/VendorInvoice";
import { Container, Form, Button, Row, Col } from "react-bootstrap";
import { toast } from "react-toastify";
import { useNavigate } from "react-router-dom";
import Sample14 from "../Components/Logo";

const VendorInvoice = () => {
  const userId = localStorage.getItem("userId") || "5";
  const [projects, setProjects] = useState([]);
  const [items, setItems] = useState([]);
  const [loadingProjects, setLoadingProjects] = useState(false);
  const [loadingItems, setLoadingItems] = useState(false);
  
  const [invoiceData, setInvoiceData] = useState({
    companyCode: "",
    transactionType: "",
    category: "", 
    projectCode: "",
    invoiceNumber: "",
    purchaseOrder: "",
    invoiceDate: "",
    postingDate: "",
    createdBy: userId,
    itemData: [{
      item: "",
      quantity: "",
      price: "",
      taxType: "",
      taxPercentage: "",
      hsn: "",
      grandTotal: "",
      createdBy: userId
    }]
  });

  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();

  // Calculate price summary values with rounding
  const calculatePriceSummary = () => {
    let totalBasePrice = 0;
    let totalTaxAmount = 0;
    let totalPrice = 0;
    let roundedTotalPrice = 0;
    let roundOff = 0;

    invoiceData.itemData.forEach(item => {
      const quantity = parseFloat(item.quantity) || 0;
      const price = parseFloat(item.price) || 0;
      const taxPercentage = parseFloat(item.taxPercentage) || 0;
      
      const basePrice = quantity * price;
      const taxAmount = basePrice * (taxPercentage / 100);
      
      totalBasePrice += basePrice;
      totalTaxAmount += taxAmount;
      totalPrice += basePrice + taxAmount;
    });

    // Calculate rounded total and round off
    roundedTotalPrice = Math.round(totalPrice);
    roundOff = roundedTotalPrice - totalPrice;

    return {
      totalBasePrice: totalBasePrice.toFixed(2),
      totalTaxAmount: totalTaxAmount.toFixed(2),
      totalPrice: totalPrice.toFixed(2),
      roundedTotalPrice: roundedTotalPrice.toFixed(2),
      roundOff: roundOff.toFixed(2)
    };
  };

  const priceSummary = calculatePriceSummary();

  useEffect(() => {
    const loadData = async () => {
      setLoadingProjects(true);
      setLoadingItems(true);
      try {
        const [projectsData, itemsData] = await Promise.all([
          fetchProjects(),
          fetchItems()
        ]);
        setProjects(projectsData);
        setItems(itemsData);
      } catch (error) {
        toast.error("Failed to load data");
        console.error("Error loading data:", error);
      } finally {
        setLoadingProjects(false);
        setLoadingItems(false);
      }
    };
    
    loadData();
  }, []);

  const companyOptions = [
    { value: "1000", label: "1000 - STPL" },
    { value: "2000", label: "2000 - STPL - Lucknow" },
    { value: "3000", label: "3000 - Discovery Wines" },
  ];

  const categoryOptions = [
    { value: "setup", label: "Setup Expense" },
    { value: "imp", label: "Imprest Expense" },
    { value: "monthly", label: "Monthly Expense" },
  ];

  const transactionTypeOptions = [
    { value: "F1", label: "F1 - Freelancer Inv" },
    { value: "I9", label: "I9 - Commission Inv" },
    { value: "A7", label: "A7 - Vendor Inv" },
    { value: "A8", label: "A8 - Imprest Inv" },
  ];  

  const handleChange = (e) => {
    setInvoiceData({ ...invoiceData, [e.target.name]: e.target.value });
  };

  const handleItemChange = (index, e) => {
    const { name, value } = e.target;
    const itemData = [...invoiceData.itemData];
    
    // Clean input for numeric fields (remove non-numeric characters except decimal point)
    const cleanedValue = ['quantity', 'price', 'taxPercentage', 'grandTotal'].includes(name)
      ? value.replace(/[^0-9.]/g, '')
      : value;

    itemData[index] = {
      ...itemData[index],
      [name]: cleanedValue,
      createdBy: userId
    };
    
    // Calculate grand total if quantity, price, or tax percentage changes
    if (name === 'quantity' || name === 'price' || name === 'taxPercentage') {
      const quantity = parseFloat(itemData[index].quantity) || 0;
      const price = parseFloat(itemData[index].price) || 0;
      const taxPercentage = parseFloat(itemData[index].taxPercentage) || 0;
      const subtotal = quantity * price;
      const taxAmount = subtotal * (taxPercentage / 100);
      const grandTotal = subtotal + taxAmount;
      itemData[index].grandTotal = Math.round(grandTotal).toString(); // Round off the grand total
    }

    setInvoiceData({ ...invoiceData, itemData });
  };

  const addNewItem = () => {
    setInvoiceData({
      ...invoiceData,
      itemData: [
        ...invoiceData.itemData,
        {
          item: "",
          quantity: "",
          price: "",
          taxType: "",
          taxPercentage: "",
          hsn: "",
          grandTotal: "",
          createdBy: userId
        }
      ]
    });
  };

  const removeItem = (index) => {
    if (invoiceData.itemData.length === 1) {
      toast.warning("At least one item is required");
      return;
    }
    
    const itemData = [...invoiceData.itemData];
    itemData.splice(index, 1);
    setInvoiceData({ ...invoiceData, itemData });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    
    try {
      await saveVendorInvoice(invoiceData);
      toast.success("Vendor Invoice Saved Successfully");
      // Reset form
      setInvoiceData({
        companyCode: "",
        transactionType: "",
        category: "",
        projectCode: "",
        invoiceNumber: "",
        purchaseOrder: "",
        invoiceDate: "",
        postingDate: "",
        createdBy: userId,
        itemData: [{
          item: "",
          quantity: "",
          price: "",
          taxType: "",
          taxPercentage: "",
          hsn: "",
          grandTotal: "",
          createdBy: userId
        }]
      });
    } catch (error) {
      toast.error("Failed to save vendor invoice");
      console.error("Error details:", error.response?.data);
    } finally {
      setLoading(false);
    }
  };

  return (
      <div className="main-content expand">
        <Container fluid className="fields-container">
          <h2 className="fields-title">Vendor Invoice</h2>

          {(loading || loadingProjects || loadingItems) && (
            <div className="loading-overlay">
              <Sample14 />
            </div>
          )}

          <Form onSubmit={handleSubmit} className="fields-form">
            <Row>
              <Col md={4}>
                <Form.Group className="mb-3">
                  <Form.Label>Company Code <span className="text-red">**</span></Form.Label>
                  <Form.Select
                    name="companyCode"
                    value={invoiceData.companyCode}
                    onChange={handleChange}
                    required
                  >
                    <option value="">Select Company</option>
                    {companyOptions.map((option) => (
                      <option key={option.value} value={option.value}>
                        {option.label}
                      </option>
                    ))}
                  </Form.Select>
                </Form.Group>
              </Col>
              <Col md={4}>
                <Form.Group className="mb-3">
                  <Form.Label>Transaction Type <span className="text-red">**</span></Form.Label>
                  <Form.Select
                    name="transactionType"
                    value={invoiceData.transactionType}
                    onChange={handleChange}
                    required
                  >
                    <option value="">Select Transaction Type</option>
                    {transactionTypeOptions.map((option) => (
                      <option key={option.value} value={option.value}>
                        {option.label}
                      </option>
                    ))}
                  </Form.Select>
                </Form.Group>
              </Col>
              <Col md={4}>
                <Form.Group className="mb-3">
                  <Form.Label>Category <span className="text-red">**</span></Form.Label>
                  <Form.Select
                    name="category"
                    value={invoiceData.category}
                    onChange={handleChange}
                    required
                  >
                    <option value="">Select Category</option>
                    {categoryOptions.map((option) => (
                      <option key={option.value} value={option.value}>
                        {option.label}
                      </option>
                    ))}
                  </Form.Select>
                </Form.Group>
              </Col>
            </Row>

            <Row>
              <Col md={4}>
                <Form.Group className="mb-3">
                  <Form.Label>Project Code <span className="text-red">**</span></Form.Label>
                  <Form.Select
                    name="projectCode"
                    value={invoiceData.projectCode}
                    onChange={handleChange}
                    required
                    disabled={loadingProjects}
                  >
                    <option value="">Select Project</option>
                    {projects.map((project) => (
                      <option key={project.prOrderId} value={project.prOrderId}>
                        {project.prOrderId} - {project.prTitle}
                      </option>
                    ))}
                  </Form.Select>
                </Form.Group>
              </Col>
              <Col md={4}>
                <Form.Group className="mb-3">
                  <Form.Label>Invoice Number</Form.Label>
                  <Form.Control
                    type="text"
                    name="invoiceNumber"
                    value={invoiceData.invoiceNumber}
                    onChange={handleChange}
                  />
                </Form.Group>
              </Col>
              <Col md={4}>
                <Form.Group className="mb-3">
                  <Form.Label>Purchase Order <span className="text-red">**</span></Form.Label>
                  <Form.Control
                    type="text"
                    name="purchaseOrder"
                    value={invoiceData.purchaseOrder}
                    onChange={handleChange}
                    required
                  />
                </Form.Group>
              </Col>
            </Row>

            <Row>
              <Col md={4}>
                <Form.Group className="mb-3">
                  <Form.Label>Invoice Date <span className="text-red">**</span></Form.Label>
                  <Form.Control
                    type="date"
                    name="invoiceDate"
                    value={invoiceData.invoiceDate}
                    onChange={handleChange}
                    required
                  />
                </Form.Group>
              </Col>
              <Col md={4}>
                <Form.Group className="mb-3">
                  <Form.Label>Posting Date <span className="text-red">**</span></Form.Label>
                  <Form.Control
                    type="date"
                    name="postingDate"
                    value={invoiceData.postingDate}
                    onChange={handleChange}
                    required
                  />
                </Form.Group>
              </Col>
            </Row>

            {/* Price Summary Section */}
            <div className="price-summary-section mb-4 p-3 border rounded">
              <h4 className="price-summary-heading mb-3">Price Summary</h4>
              <Row>
                <Col md={3}>
                  <Form.Group className="mb-3">
                    <Form.Label>Total Base Price</Form.Label>
                    <Form.Control
                      type="text"
                      value={priceSummary.totalBasePrice}
                      readOnly
                      className="font-weight-bold"
                    />
                  </Form.Group>
                </Col>
                <Col md={3}>
                  <Form.Group className="mb-3">
                    <Form.Label>Total Tax Amount</Form.Label>
                    <Form.Control
                      type="text"
                      value={priceSummary.totalTaxAmount}
                      readOnly
                      className="font-weight-bold"
                    />
                  </Form.Group>
                </Col>
                <Col md={3}>
                  <Form.Group className="mb-3">
                    <Form.Label>Round off</Form.Label>
                    <Form.Control
                      type="text"
                      value={priceSummary.roundOff}
                      readOnly
                      className="font-weight-bold"
                    />
                  </Form.Group>
                </Col>
                <Col md={3}>
                  <Form.Group className="mb-3">
                    <Form.Label>Total Amount</Form.Label>
                    <Form.Control
                      type="text"
                      value={priceSummary.roundedTotalPrice}
                      readOnly
                      className="font-weight-bold"
                    />
                  </Form.Group>
                </Col>
              </Row>
            </div>

             <div className="item-data-section">
              <h4 className="item-data-heading mb-3">Item Data</h4>
              
              {invoiceData.itemData.map((item, index) => (
                <div key={index} className="item-data-container mb-4 p-3 border rounded">
                  <Row className="align-items-end">
                    {/* Item */}
                    <Col md={2}>
                      <Form.Group className="mb-3">
                        <Form.Label>Item <span className="text-red">**</span></Form.Label>
                        <Form.Select
                          name="item"
                          value={item.item}
                          onChange={(e) => handleItemChange(index, e)}
                          required
                          disabled={loadingItems}
                        >
                          <option value="">Select Item</option>
                          {items.map((itemOption) => (
                            <option key={itemOption.itemCode} value={itemOption.itemCode}>
                              {itemOption.itemCode} - {itemOption.itemName}
                            </option>
                          ))}
                        </Form.Select>
                      </Form.Group>
                    </Col>

                    {/* Quantity */}
                    <Col md={1}>
                      <Form.Group className="mb-3">
                        <Form.Label>Qty <span className="text-red">**</span></Form.Label>
                        <Form.Control
                          type="text"
                          name="quantity"
                          value={item.quantity}
                          onChange={(e) => handleItemChange(index, e)}
                          required
                        />
                      </Form.Group>
                    </Col>

                    {/* Price */}
                    <Col md={1}>
                      <Form.Group className="mb-3">
                        <Form.Label>Price <span className="text-red">**</span></Form.Label>
                        <Form.Control
                          type="text"
                          name="price"
                          value={item.price}
                          onChange={(e) => handleItemChange(index, e)}
                          required
                        />
                      </Form.Group>
                    </Col>

                    {/* Tax Type */}
                    <Col md={1}>
                      <Form.Group className="mb-3">
                        <Form.Label>Tax Type <span className="text-red">**</span></Form.Label>
                        <Form.Control
                          type="text"
                          name="taxType"
                          value={item.taxType}
                          onChange={(e) => handleItemChange(index, e)}
                          required
                        />
                      </Form.Group>
                    </Col>

                    {/* Tax Percentage */}
                    <Col md={1}>
                      <Form.Group className="mb-3">
                        <Form.Label>Tax % <span className="text-red">**</span></Form.Label>
                        <Form.Control
                          type="text"
                          name="taxPercentage"
                          value={item.taxPercentage}
                          onChange={(e) => handleItemChange(index, e)}
                          required
                        />
                      </Form.Group>
                    </Col>

                    {/* HSN */}
                    <Col md={2}>
                      <Form.Group className="mb-3">
                        <Form.Label>HSN <span className="text-red">**</span></Form.Label>
                        <Form.Control
                          type="text"
                          name="hsn"
                          value={item.hsn}
                          onChange={(e) => handleItemChange(index, e)}
                          required
                        />
                      </Form.Group>
                    </Col>

                    {/* Grand Total */}
                    <Col md={1}>
                      <Form.Group className="mb-3">
                        <Form.Label>Grand Total</Form.Label>
                        <Form.Control
                          type="text"
                          name="grandTotal"
                          value={item.grandTotal}
                          readOnly
                        />
                      </Form.Group>
                    </Col>

                    {/* Action Buttons */}
                    <Col md={2}>
                      <div className="d-flex gap-2">
                        <Button 
                          variant="danger" 
                          onClick={() => removeItem(index)}
                          className="item-action-btn"
                          disabled={invoiceData.itemData.length === 1}
                        >
                          Remove
                        </Button>
                        {index === invoiceData.itemData.length - 1 && (
                          <Button 
                            variant="success" 
                            onClick={addNewItem}
                            className="item-action-btn"
                          >
                            Add
                          </Button>
                        )}
                      </div>
                    </Col>
                  </Row>
                </div>
              ))}
            </div>

            <div className="fields-btn-container">
              <Button
                variant="primary"
                type="submit"
                className="fields-submit"
                disabled={loading || loadingProjects || loadingItems}
              >
                {loading ? "Saving..." : "Save Invoice"}
              </Button>
            </div>
          </Form>
        </Container>
      </div>
  );
};

export default VendorInvoice;